import { type Dispatch, type SetStateAction } from 'react';
import type { Edge, Node, XYPosition } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import type { StepLayoutGroupColor } from '@erp/workflows';
import {
  dissolveStepLayoutGroups,
  getBoundingRectForNodes,
  getMembershipCandidatesForStepLayoutGroup,
  getStepLayoutSelectionRoots,
  isStepLayoutGroupNode,
  planStepLayoutGroupDeletion,
  reassignStepLayoutGroupMembership,
  STEP_LAYOUT_GROUP_DEFAULT_SIZE,
  STEP_LAYOUT_GROUP_NODE_TYPE,
  type Rect,
  type StepLayoutGroupNodeCallbacks,
} from 'src/lib/workflows/stepLayoutGroups';

export function useCreateGroupFromMenu(
  contextMenu: XYPosition | null,
  screenToFlowPosition: (position: XYPosition) => XYPosition,
  createStepLayoutGroupAt: (position: XYPosition) => void,
) {
  return () => {
    if (!contextMenu) return;
    createStepLayoutGroupAt(
      screenToFlowPosition({ x: contextMenu.x, y: contextMenu.y }),
    );
  };
}

/**
 * Where a new group should be created, in the coordinate space of the node that
 * will own it. The plain workflow editor puts groups at the top level; the
 * workflow-group editor nests them inside the frame of the workflow being
 * edited, which is what `parentId`/`workflowId` are for.
 */
export interface StepLayoutGroupPlacement {
  parentId?: string;
  position: XYPosition;
  workflowId?: string;
}

interface UseStepLayoutGroupsOptions<TNode extends Node> {
  /**
   * Current nodes. Read only to decide *whether* a mutation applies before an
   * undo checkpoint is pushed — the mutations themselves all go through
   * `setNodes` updaters so they never act on a stale list.
   */
  nodes: TNode[];
  setNodes: Dispatch<SetStateAction<TNode[]>>;
  isReadOnly?: boolean;
  /** Undo checkpoint, pushed before each group mutation. */
  pushSnapshot?: () => void;
  /**
   * Maps a canvas point to the container that should own a group created there,
   * returning null to refuse (e.g. a click outside every workflow frame).
   * Defaults to a top-level group at the point.
   */
  resolvePlacement?: (position: XYPosition) => StepLayoutGroupPlacement | null;
  /** Refuse to group a selection — e.g. one spanning two workflows. */
  canGroupNodes?: (nodes: TNode[]) => boolean;
  /** Ran after any group mutation, for dirty-marking / frame refitting. */
  onStepLayoutGroupsChanged?: () => void;
  /** Default label for a newly created group. */
  newStepLayoutGroupLabel: string;
}

export interface UseStepLayoutGroupsResult<TNode extends Node> {
  /** Wrap the selected step nodes in a new group. */
  groupSelectedNodes: () => void;
  /** Create an empty group at a canvas point. */
  createStepLayoutGroupAt: (position: XYPosition) => void;
  /** Remove the group box, leaving its steps on the canvas. */
  deleteStepLayoutGroup: (groupId: string) => void;
  /** Called on drag end to move steps in and out of groups they were dropped on. */
  handleNodeDragStop: (draggedNodes: TNode[]) => void;
  /**
   * Pass to ReactFlow's `onBeforeDelete` so deleting a group doesn't take its
   * steps with it.
   */
  handleBeforeDelete: (params: {
    nodes: TNode[];
    edges: Edge[];
  }) => Promise<{ nodes: TNode[]; edges: Edge[] } | boolean>;
  /** Inject into group node data so the label and colour picker work. */
  stepLayoutGroupCallbacks: StepLayoutGroupNodeCallbacks;
  /** True when at least two step nodes are selected — the "Group Nodes" gate. */
  canGroupSelection: (nodes: TNode[]) => boolean;
}

/**
 * All mutation logic for step layout groups, shared by the workflow editor and the
 * workflow-group editor.
 *
 * Every operation is a pure `setNodes` transform, so groups ride the editors'
 * existing machinery for free: undo/redo snapshots the node list, the dirty
 * flag watches it, and the save path reads groups back out of it via
 * `extractStepLayoutGroups`. There is no separate group state to keep in sync.
 */
export function useStepLayoutGroups<TNode extends Node>({
  nodes,
  setNodes,
  isReadOnly = false,
  pushSnapshot,
  resolvePlacement,
  canGroupNodes,
  onStepLayoutGroupsChanged,
  newStepLayoutGroupLabel,
}: UseStepLayoutGroupsOptions<TNode>): UseStepLayoutGroupsResult<TNode> {
  const buildGroupNode = (
    placement: StepLayoutGroupPlacement,
    size: { width: number; height: number },
  ) =>
    ({
      id: uuidv4(),
      type: STEP_LAYOUT_GROUP_NODE_TYPE,
      position: placement.position,
      ...(placement.parentId ? { parentId: placement.parentId } : {}),
      // No `zIndex`, and interior transparent to canvas gestures — see
      // `buildStepLayoutGroupNode` for why both matter.
      style: {
        width: size.width,
        height: size.height,
        pointerEvents: 'none' as const,
      },
      dragHandle: '.step-layout-group-drag-handle',
      data: {
        groupId: '',
        label: newStepLayoutGroupLabel,
        color: null,
        workflowId: placement.workflowId,
        inputs: {},
        outputs: {},
      },
    }) as unknown as TNode;

  const canGroupSelection = (nodes: TNode[]) => {
    if (isReadOnly) return false;
    // Counted as entities, not nodes: a selected box is one thing, and the
    // steps inside it don't add to the tally (see
    // `getStepLayoutSelectionRoots`). Boxes are groupable alongside steps,
    // which is what produces a nested box.
    const roots = getStepLayoutSelectionRoots(nodes);
    if (roots.length < 2) return false;
    // A group owns its members as subflow children, so they must all start
    // out in one coordinate space — otherwise reparenting them would move
    // them on screen.
    const parents = new Set(roots.map((node) => node.parentId ?? null));
    if (parents.size > 1) return false;
    return canGroupNodes?.(roots) ?? true;
  };

  const groupSelectedNodes = () => {
    if (isReadOnly) return;
    // Checked up front so a refused grouping doesn't leave a no-op entry in the
    // undo stack. The updater re-checks against the live list.
    if (!canGroupSelection(nodes)) return;
    pushSnapshot?.();

    setNodes((current) => {
      if (!canGroupSelection(current)) return current;

      const selected = getStepLayoutSelectionRoots(current);
      const rect = getBoundingRectForNodes(selected);
      if (!rect) return current;

      const selectedIds = new Set(selected.map((node) => node.id));
      const groupNode = buildGroupNode(
        {
          // The selection already shares one parent (enforced above), so the
          // bounding box is in that parent's space and the group belongs there
          // too.
          parentId: selected[0].parentId,
          position: { x: rect.x, y: rect.y },
          workflowId: (selected[0].data as { workflowId?: string })?.workflowId,
        },
        { width: rect.width, height: rect.height },
      );
      const groupId = groupNode.id;
      const groupNodeWithId = {
        ...groupNode,
        data: { ...groupNode.data, groupId },
      };

      const rest = current.map((node) => {
        if (!selectedIds.has(node.id)) return node;
        return {
          ...node,
          parentId: groupId,
          position: {
            x: node.position.x - rect.x,
            y: node.position.y - rect.y,
          },
          selected: false,
        };
      });

      // Group first so the backdrop paints behind ungrouped nodes that happen
      // to overlap it.
      return [groupNodeWithId, ...rest];
    });

    onStepLayoutGroupsChanged?.();
  };

  const createStepLayoutGroupAt = (position: XYPosition) => {
    if (isReadOnly) return;
    const placement = resolvePlacement
      ? resolvePlacement(position)
      : { position };
    if (!placement) return;

    pushSnapshot?.();
    // Centred on the click rather than hung off it: the point the user picked
    // is where they want the region, not where they want its top-left corner.
    const half = STEP_LAYOUT_GROUP_DEFAULT_SIZE / 2;
    const groupNode = buildGroupNode(
      {
        ...placement,
        position: {
          x: Math.round(placement.position.x - half),
          y: Math.round(placement.position.y - half),
        },
      },
      {
        width: STEP_LAYOUT_GROUP_DEFAULT_SIZE,
        height: STEP_LAYOUT_GROUP_DEFAULT_SIZE,
      },
    );
    const groupNodeWithId = {
      ...groupNode,
      // Selected on arrival so the resize handles are already showing — an
      // empty box you can't obviously resize looks broken.
      selected: true,
      data: { ...groupNode.data, groupId: groupNode.id },
    };
    setNodes((current) =>
      // A fresh box adopts whatever it landed over, so it can't sit there
      // visually containing steps that aren't members.
      reassignStepLayoutGroupMembership(
        [
          groupNodeWithId,
          ...current.map((node) =>
            node.selected ? { ...node, selected: false } : node,
          ),
        ],
        getMembershipCandidatesForStepLayoutGroup(
          [groupNodeWithId, ...current],
          groupNodeWithId.id,
        ),
      ),
    );
    onStepLayoutGroupsChanged?.();
  };

  const renameStepLayoutGroup = (groupId: string, label: string) => {
    if (isReadOnly) return;
    pushSnapshot?.();
    setNodes((current) =>
      current.map((node) =>
        node.id === groupId ? { ...node, data: { ...node.data, label } } : node,
      ),
    );
    onStepLayoutGroupsChanged?.();
  };

  const recolorStepLayoutGroup = (
    groupId: string,
    color: StepLayoutGroupColor | null,
  ) => {
    if (isReadOnly) return;
    pushSnapshot?.();
    setNodes((current) =>
      current.map((node) =>
        node.id === groupId ? { ...node, data: { ...node.data, color } } : node,
      ),
    );
    onStepLayoutGroupsChanged?.();
  };

  /**
   * Lift a group's members back into the group's own parent space and drop the
   * group. Shared by explicit deletion and the keyboard-delete guard — in both
   * cases the steps must survive: a group is a label, and deleting a label is
   * not deleting the work.
   */
  const deleteStepLayoutGroup = (groupId: string) => {
    if (isReadOnly) return;
    pushSnapshot?.();
    setNodes((current) =>
      dissolveStepLayoutGroups(current, new Set([groupId])),
    );
    onStepLayoutGroupsChanged?.();
  };

  const handleBeforeDelete = async ({
    nodes: nodesToDelete,
    edges,
  }: {
    nodes: TNode[];
    edges: Edge[];
  }) => {
    const plan = planStepLayoutGroupDeletion(nodesToDelete, edges);
    if (plan.groupIds.size === 0) return { nodes: nodesToDelete, edges };

    // Checkpoint here rather than relying on the host: every other mutation in
    // this hook pushes its own, and `WorkflowEditor` only got away without one
    // because `onNodesDelete` happens to push afterwards while the ref still
    // holds pre-dissolve nodes. A second host would silently lose undo for
    // keyboard-deleting a box.
    pushSnapshot?.();

    // Lift the members out before React Flow removes their parent, so they
    // don't end up pointing at a node that no longer exists.
    setNodes((current) => dissolveStepLayoutGroups(current, plan.groupIds));
    onStepLayoutGroupsChanged?.();

    return { nodes: plan.nodes, edges: plan.edges };
  };

  const handleNodeDragStop = (draggedNodes: TNode[]) => {
    if (isReadOnly) return;

    // One pass, partitioned: a drag is either steps, boxes, or both, and each
    // side feeds a different half of the membership recompute below.
    const draggedStepIds: string[] = [];
    const draggedGroupIds: string[] = [];
    for (const node of draggedNodes) {
      if (isStepLayoutGroupNode(node)) draggedGroupIds.push(node.id);
      else draggedStepIds.push(node.id);
    }
    if (draggedStepIds.length === 0 && draggedGroupIds.length === 0) return;

    setNodes((current) => {
      // Moving a *box* over steps adopts them, just as moving a *step* into a
      // box does — membership is geometric, so it can't depend on which of the
      // two things the user happened to drag. (Existing members travel with the
      // box, so they stay put in its coordinate space and are unaffected.)
      const candidates = new Set(draggedStepIds);
      for (const groupId of draggedGroupIds) {
        for (const stepId of getMembershipCandidatesForStepLayoutGroup(
          current,
          groupId,
        )) {
          candidates.add(stepId);
        }
      }
      return reassignStepLayoutGroupMembership(current, candidates);
    });
    onStepLayoutGroupsChanged?.();
  };

  /**
   * Resizing settles membership from the box's new bounds — which means shrinking
   * a box past a member *releases* it. That's deliberate: membership is defined
   * by geometry, and the drag path already behaves this way, so having resize
   * keep a member that now sits visibly outside the box would make the two
   * gestures disagree about what the box means. The step is only released from
   * the group, never deleted, and undo restores the grouping.
   */
  const handleStepLayoutGroupResizeEnd = (groupId: string, rect: Rect) => {
    if (isReadOnly) return;
    pushSnapshot?.();
    setNodes((current) =>
      // `rect` comes straight from the resizer, so the decision uses the box
      // the user just dragged rather than React Flow's measured dimensions,
      // which may not have settled yet.
      reassignStepLayoutGroupMembership(
        current,
        getMembershipCandidatesForStepLayoutGroup(current, groupId),
        new Map([[groupId, rect]]),
      ),
    );
    onStepLayoutGroupsChanged?.();
  };

  const stepLayoutGroupCallbacks: StepLayoutGroupNodeCallbacks = isReadOnly
    ? {}
    : {
        onRenameStepLayoutGroup: renameStepLayoutGroup,
        onRecolorStepLayoutGroup: recolorStepLayoutGroup,
        onResizeStepLayoutGroupEnd: handleStepLayoutGroupResizeEnd,
      };

  return {
    groupSelectedNodes,
    createStepLayoutGroupAt,
    deleteStepLayoutGroup,
    handleNodeDragStop,
    handleBeforeDelete,
    stepLayoutGroupCallbacks,
    canGroupSelection,
  };
}
